/**
 * Maps SBI Core dashboard metrics to sbi_responses data (question_id / answer_id).
 * Extend these mappings to drive visualizations from Supabase sbi_responses.
 *
 * Types:
 * - percent_above: metric value = % of cases where numeric answer_value >= threshold (e.g. "High Pain")
 * - percent_below: metric value = % of cases where numeric answer_value <= threshold (e.g. "No High Pain")
 * - percent_answer: metric value = % of cases that selected the given answer_id(s) (use answerIds array)
 * - average: metric value = average of numeric answer_value, count = number of cases with a value
 */

// Patient Satisfaction: NRS 0–10, display % satisfied (value >= 7)
// SBI-Q31 = Zufriedenheit Prämedikation, Q99 = OP/Anästhesie, Q144 = Aufwachraum
export const PATIENT_SATISFACTION_QUESTIONS = [
  {
    label: "Pre-med talk",
    questionId: "SBI-Q31",
    type: "percent_above",
    threshold: 7,
  },
  {
    label: "Induction Anaesthesia",
    questionId: "SBI-Q99",
    type: "percent_above",
    threshold: 7,
  },
  {
    label: "PACU Stays",
    questionId: "SBI-Q144",
    type: "percent_above",
    threshold: 7,
  },
];

// Data Quality: SBI-Q56 = Qualitätsbewertung (Gut/Mässig/Schlecht)
export const DATA_QUALITY_QUESTIONS = [
  { label: "Internal Consistency", questionId: null },
  {
    label: "Quality of Evaluation",
    questionId: "SBI-Q56",
    type: "percent_answer",
    answerIds: ["SBI-A56.1"],
  }, // Gut
  { label: "Completeness of Data", questionId: null },
];

// QUATTRO: No Delirium, No Nausea, No High Pain, No High Discomfort, QUATTRO
export const QUATTRO_QUESTIONS = [
  { label: "No Delirium / PND", questionId: null },
  {
    label: "No Nausea / Vomiting",
    questionId: "SBI-Q95",
    type: "percent_below",
    threshold: 3,
  },
  {
    label: "No High Pain",
    questionId: "SBI-Q89",
    type: "percent_below",
    threshold: 3,
  },
  {
    label: "No High Discomfort",
    questionId: "SBI-Q94",
    type: "percent_above",
    threshold: 7,
  }, // Wohlbefinden >= 7 = no high discomfort
  { label: "QUATTRO", questionId: null },
];

// Patient Comfort and Outcome: { title, value, count }
// NRS 0–10: percent_above threshold = % with high symptom
export const PATIENT_COMFORT_QUESTIONS = [
  { title: "Delirium / PND", questionId: null },
  {
    title: "High Anxiety",
    questionId: "SBI-Q92",
    type: "percent_above",
    threshold: 6,
  },
  { title: "Long Fluid Fasting Time", questionId: null },
  {
    title: "High Thirst",
    questionId: "SBI-Q97",
    type: "percent_above",
    threshold: 6,
  },
  {
    title: "High Pain",
    questionId: "SBI-Q89",
    type: "percent_above",
    threshold: 6,
  },
  {
    title: "Burst Suppression Time",
    questionId: "SBI-Q55",
    type: "average",
  },
  {
    title: "Throat/Airway discomfort",
    questionId: "SBI-Q98",
    type: "percent_above",
    threshold: 6,
  },
  {
    title: "Shivering",
    questionId: "SBI-Q91",
    type: "percent_above",
    threshold: 1,
  },
  {
    title: "High Stress",
    questionId: "SBI-Q93",
    type: "percent_above",
    threshold: 6,
  },
  {
    title: "Nausea / Vomiting",
    questionId: "SBI-Q95",
    type: "percent_above",
    threshold: 6,
  },
  {
    title: "Reduced well-being",
    questionId: "SBI-Q94",
    type: "percent_below",
    threshold: 4,
  },
  { title: "Hypothermia avoided", questionId: null },
];

// Prevention: SBI-Q4 = Präventionsmaßnahmen (multi-choice), answerIds from SBI-A4.x
// SBI-A4.3 = PONV SOP, A4.6 = Kapnographie, A4.7 = Augenmasken/Ohrstöpsel (noise). Q50 = Lärmreduktion (yes/no).
export const PREVENTION_QUESTIONS = [
  {
    title: "PONV SOP applied",
    questionId: "SBI-Q4",
    type: "percent_answer",
    answerIds: ["SBI-A4.3"],
  },
  { title: "EEG used (w/sedation, general anaesthesia)", questionId: null },
  {
    title: "Capnography used (w/sedation)",
    questionId: "SBI-Q4",
    type: "percent_answer",
    answerIds: ["SBI-A4.6"],
  },
  {
    title: "Noise Reduction Performed",
    questionId: "SBI-Q50",
    type: "percent_answer",
    answerIds: ["SBI-A50.1"],
  }, // Ja
  { title: "Cont. Temp. Monitoring", questionId: null },
  { title: "Cont. Intraop. Monitoring used", questionId: null },
];
